import { useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent } from "constants/index";
import { useAuthContext, useRoleContext, useWriterComponentState } from "hooks";
import { emailIsValid } from "utils";

import type { AxiosError } from "axios";

const MAX_RECIPIENTS = 14;
const CHIP_SEPARATORS = /[,\s;]+/;

interface ShareTfcViaEmailProps {
  tfcId: number;
  shipmentId: number;
}

interface ShareErrorResponse {
  message?: string;
  offending_email?: string;
}

export function ShareTfcViaEmail(props: ShareTfcViaEmailProps) {
  const { tfcId, shipmentId } = props;
  const { user } = useAuthContext();
  const { role } = useRoleContext();
  const posthog = usePostHog();
  const [chips, setChips] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [invalidChip, setInvalidChip] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [ccRequester, setCcRequester] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    loading,
    write,
    error: submitError,
  } = useWriterComponentState({
    errorMessage: "Failed to send. Please try again.",
    writeConfig: {
      method: "post",
      url: `/tfc/${tfcId}/share`,
      body: { recipient_emails: chips, cc_requester: ccRequester },
    },
    onWriteFailure: (e) => {
      const axiosError = e as AxiosError<ShareErrorResponse>;
      const offending = axiosError?.response?.data?.offending_email;
      if (offending) setInvalidChip(offending.toLowerCase());
    },
  });

  const addChipsFromParts = (parts: string[]): string[] => {
    if (parts.length === 0) return [];
    const next = [...chips];
    const seen = new Set(chips);
    const invalidParts: string[] = [];
    parts.forEach((part) => {
      const trimmed = part.trim();
      if (!trimmed) return;
      const normalized = trimmed.toLowerCase();
      if (seen.has(normalized)) return;
      if (!emailIsValid(normalized)) {
        invalidParts.push(trimmed);
        return;
      }
      next.push(normalized);
      seen.add(normalized);
    });
    setChips(next);
    setParseError(
      invalidParts.length > 0
        ? `Invalid email${invalidParts.length > 1 ? "s" : ""}: ${invalidParts.join(", ")}`
        : null,
    );
    return invalidParts;
  };

  const flushInput = () => {
    const parts = inputValue.split(CHIP_SEPARATORS).filter(Boolean);
    const invalidParts = addChipsFromParts(parts);
    setInputValue(invalidParts.join(" "));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "Tab" ||
      e.key === "," ||
      e.key === ";"
    ) {
      if (inputValue.trim()) {
        e.preventDefault();
        flushInput();
      }
      return;
    }
    if (e.key === "Backspace" && inputValue === "" && chips.length > 0) {
      setChips((prev) => prev.slice(0, -1));
      setInvalidChip(null);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (CHIP_SEPARATORS.test(pasted)) {
      e.preventDefault();
      const invalidParts = addChipsFromParts(
        pasted.split(CHIP_SEPARATORS).filter(Boolean),
      );
      setInputValue((prev) =>
        [prev.trim(), ...invalidParts].filter(Boolean).join(" "),
      );
    }
  };

  const handleRemoveChip = (chip: string) => {
    setChips(chips.filter((c) => c !== chip));
    if (invalidChip === chip) setInvalidChip(null);
  };

  const handleSubmit = () => {
    setSuccessMessage(null);
    setInvalidChip(null);
    write<{ recipient_count: number }>((data) => {
      posthog?.capture(ClientAppEvent.TFC_SHARED, {
        tfc_id: tfcId,
        shipment_id: shipmentId,
        recipient_count: data.recipient_count,
        cc_requester: ccRequester,
        requester_role_id: role?.id,
        requester_company_type: role?.type,
      });
      setSuccessMessage(`Sent to ${data.recipient_count} recipient(s).`);
      setChips([]);
    });
  };

  const overCap = chips.length > MAX_RECIPIENTS;
  const submitDisabled = chips.length === 0 || overCap || loading;
  let tooltipTitle: string;
  if (chips.length === 0) {
    tooltipTitle = "Add at least one email to send.";
  } else if (overCap) {
    tooltipTitle = `Remove ${chips.length - MAX_RECIPIENTS} email(s) — max is ${MAX_RECIPIENTS}.`;
  } else {
    tooltipTitle = `Send the TFC summary to ${chips.length} recipient(s).`;
  }

  return (
    <Box
      sx={{
        border: "1px solid #D1D5DB",
        borderRadius: "4px",
        p: 2,
        mt: 2,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 1 }}>
        Notify Accounting
      </Typography>

      {chips.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
          {chips.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              onDelete={() => handleRemoveChip(chip)}
              size="small"
              sx={
                invalidChip === chip
                  ? {
                      backgroundColor: "#FFEBEE",
                      borderColor: "#D32F2F",
                      color: "#D32F2F",
                    }
                  : undefined
              }
              variant={invalidChip === chip ? "outlined" : "filled"}
            />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} alignItems="stretch">
        <TextField
          fullWidth
          size="small"
          placeholder="Enter email addresses (press Space, Tab, or Enter to add)"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (parseError) setParseError(null);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={flushInput}
          disabled={loading}
        />
        <Tooltip title={tooltipTitle}>
          <span>
            <Button
              variant="contained"
              disabled={submitDisabled}
              onClick={handleSubmit}
              sx={{ px: 3, height: "100%", whiteSpace: "nowrap" }}
            >
              {loading ? "SENDING…" : "SEND EMAIL"}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {chips.length > 0 && (
        <Typography
          sx={{
            fontSize: "12px",
            color: overCap ? "#D32F2F" : "#6B7280",
            mt: 0.5,
          }}
        >
          {overCap
            ? `Remove ${chips.length - MAX_RECIPIENTS} to send (max ${MAX_RECIPIENTS} recipients)`
            : `${chips.length} / ${MAX_RECIPIENTS} recipients`}
        </Typography>
      )}

      <ErrorTypography error={parseError} />
      <ErrorTypography error={submitError} />

      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Checkbox
            checked={ccRequester}
            onChange={(e) => setCcRequester(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography sx={{ fontSize: "14px" }}>
            {user?.email
              ? `CC me on the email (${user.email})`
              : "CC me on the email"}
          </Typography>
        }
      />

      {successMessage && (
        <Typography sx={{ fontSize: "14px", color: "#2E7D32", mt: 1 }}>
          {successMessage}
        </Typography>
      )}
    </Box>
  );
}
